import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useContext } from "react";
import { PORTAL_CODES_MERCHANT_ID } from "../utils/permissions";
import { DEFAULT_LANDING_PATH } from "../utils/routes";
import MerchantWrapper from "./layouts/MerchantWrapper";
import AppLayout from "./layouts/AppLayout";
import ConfigurationLayout from "./layouts/ConfigurationLayout";
import AnalyticsContainer from "./app/analytics/AnalyticsContainer";
import Authenticate from "./auth/Authenticate";
import AuthenticateSignUp from "./auth/AuthenticateSignUp";
import AuthCallback from "./auth/AuthCallback";
import Fallback from "./layouts/Fallback";
import ActivitiesContainer from "./app/ActivitiesContainer";
import ReportsContainer from "./app/ReportsContainer";
import ConfigurationContainer from "./app/ConfigurationContainer";
import ConfigurationRedirect from "./app/ConfigurationRedirect";
import IntegrationsConfigurationContainer from "./app/IntegrationsConfigurationContainer";
import AgentsConfigurationContainer from "./app/AgentsConfigurationContainer";
import ProjectsConfigurationContainer from "./app/ProjectsConfigurationContainer";
import ConsumptionContainer from "./app/ConsumptionContainer";
import InboxContainer from "./app/inbox/InboxContainer";
import TableroContainer from "./app/tablero/TableroContainer";
import AssignmentContainer from "./app/AssignmentContainer";
import WhatsappConnectionsContainer from "./app/WhatsappConnectionsContainer";
import WhatsappTemplatesContainer from "./app/WhatsappTemplatesContainer";
import SignUpContainer from "./auth/SignUpContainer";
import AppointmentConfigurationContainer from "./app/AppointmentConfigurationContainer";
import UsersContainer from "./app/UsersContainer";
import LeadQualificationContainer from "./app/LeadQualificationContainer";
import UserContext from "../contexts/UserContext";
import MerchantContext from "../contexts/MerchantContext";
import BroadcastContainer from "./app/BroadcastContainer";
import { Spinner } from "../components/ui/spinner";
import RecaudoContainer from "./app/RecaudoContainer";
import InboundCampaignRulesContainer from "./app/InboundCampaignRulesContainer";
import AuditoriaContainer from "./app/AuditoriaContainer";
import PortalCodesContainer from "./app/PortalCodesContainer";

const ProtectedRoute = ({ element }: { element: JSX.Element }) => {
  const { roles } = useContext(UserContext);
  const isBasicEmployee = roles.includes("basic_employee");

  if (isBasicEmployee) {
    return <Navigate to="/inbox" replace />;
  }

  return element;
};

const BroadcastProtectedRoute = ({ element }: { element: JSX.Element }) => {
  const { broadcastEnabled } = useContext(MerchantContext);

  if (!broadcastEnabled) {
    return <Navigate to="/inbox" replace />;
  }

  return element;
};

const CollectionsProtectedRoute = ({ element }: { element: JSX.Element }) => {
  const { collectionsEnabled, isLoading } = useContext(MerchantContext);

  if (isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Spinner size="xl" />
      </div>
    );
  }

  if (!collectionsEnabled) {
    return <Navigate to="/inbox" replace />;
  }

  return element;
};

const PortalCodesProtectedRoute = ({ element }: { element: JSX.Element }) => {
  const { merchantId, isLoading } = useContext(MerchantContext);

  if (isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Spinner size="xl" />
      </div>
    );
  }

  if (merchantId !== PORTAL_CODES_MERCHANT_ID) {
    return <Navigate to="/inbox" replace />;
  }

  return element;
};

const PalommaProtectedRoute = ({ element }: { element: JSX.Element }) => {
  const { email, hasPalommaOnlyAccess, isLoading } = useContext(UserContext);

  // On a hard load (deep link) the user info arrives async — deciding before it
  // resolves would bounce every guarded deep link to /inbox. isLoading and the
  // flag live in different stores (query flag vs onSuccess setState), so there's
  // a render where isLoading is false but onSuccess hasn't run yet: wait for the
  // email, which lands in the same onSuccess as the flag (a false flag alone can't
  // tell "denied" from "not loaded"). Unauthenticated sessions exit via
  // MerchantWrapper → /signup.
  if (isLoading || !email) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Spinner size="xl" />
      </div>
    );
  }

  if (!hasPalommaOnlyAccess) {
    return <Navigate to="/inbox" replace />;
  }

  return element;
};

const LegacyLeadsRedirect = () => {
  const { search } = useLocation();
  return <Navigate to={`/inbox${search}`} replace />;
};

const RouterContainer = () => {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public routes */}
        <Route path="/signup" element={<SignUpContainer />} />
        <Route path="/authenticate" element={<Authenticate />} />
        <Route path="/auth/discovery" element={<AuthenticateSignUp />} />
        <Route path="/auth/callback" element={<AuthCallback />} />

        {/* Root redirect */}
        <Route path="/" element={<Navigate to={DEFAULT_LANDING_PATH} replace />} />

        {/* Protected routes with session guard */}
        <Route element={<MerchantWrapper />}>
          {/* Main app routes */}
          <Route element={<AppLayout />}>
            <Route path="/analytics" element={<AnalyticsContainer />} />
            <Route path="/activities" element={<ActivitiesContainer />} />
            <Route path="/leads" element={<LegacyLeadsRedirect />} />
            <Route path="/tablero" element={<TableroContainer />} />
            <Route path="/inbox" element={<InboxContainer />} />
            <Route
              path="/broadcast"
              element={<BroadcastProtectedRoute element={<BroadcastContainer />} />}
            />
            <Route
              path="/recaudo"
              element={<CollectionsProtectedRoute element={<RecaudoContainer />} />}
            />
            <Route path="/reports" element={<ReportsContainer />} />
            {/* Protected routes - not accessible depending on user role */}
            <Route
              path="/consumption"
              element={<ProtectedRoute element={<ConsumptionContainer />} />}
            />
            {/* Configuration routes with nested layout - protected depending on user role */}
            <Route
              path="/configuration"
              element={<ProtectedRoute element={<ConfigurationLayout />} />}
            >
              <Route index element={<ConfigurationRedirect />} />
              <Route path="pipeline" element={<ConfigurationContainer />} />
              <Route path="integrations" element={<IntegrationsConfigurationContainer />} />
              <Route path="assignment" element={<AssignmentContainer />} />
              <Route
                path="agents"
                element={<PalommaProtectedRoute element={<AgentsConfigurationContainer />} />}
              />
              <Route
                path="projects"
                element={<PalommaProtectedRoute element={<ProjectsConfigurationContainer />} />}
              />
              <Route path="whatsapp" element={<WhatsappConnectionsContainer />} />
              <Route path="templates" element={<WhatsappTemplatesContainer />} />
              <Route path="schedules" element={<AppointmentConfigurationContainer />} />
              <Route path="users" element={<UsersContainer />} />
              <Route path="lead-qualification" element={<LeadQualificationContainer />} />
              <Route path="inbound-campaign-rules" element={<InboundCampaignRulesContainer />} />
              <Route path="auditoria" element={<AuditoriaContainer />} />
              <Route
                path="portal-codes"
                element={<PortalCodesProtectedRoute element={<PortalCodesContainer />} />}
              />
            </Route>
          </Route>
          {/* Redirect for unmatched routes */}
          <Route path="*" element={<Fallback />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
};

export default RouterContainer;
